import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import {
  faBan,
  faCheck,
  faPen,
  faPlus,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { faEdit, faPhone } from "@fortawesome/pro-duotone-svg-icons";
import { NbToastrService } from "@nebular/theme";
import {
  ColumnModel,
  FilterSettingsModel,
  GridComponent,
  PageSettingsModel,
} from "@syncfusion/ej2-angular-grids";
import { forkJoin } from "rxjs";
import { ErrorService } from "../../../../../@core/utils/error.service";
import { KmlService } from "../../../../../@core/utils/kml.service";
import { SessionService } from "../../../../../@core/utils/session.service";
import {
  ConfirmationData,
  ConfirmationDialogComponent,
} from "../../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component";
import { Carrier } from "../../../../modules/pick-plan/models/carrier";
import { TruckType } from "../../../../modules/pick-plan/models/truck-type";
import { carrierService } from "../../../../modules/pick-plan/services/carrier.service";
import { truckTypeService } from "../../../../modules/pick-plan/services/truck-type.service";
import { PlatformConfigService } from "../../../services/platform-config.service";
import { KwFilterDropdownService } from "../../../widgets/kw-filter-dropdown/kw-filter-dropdown.service";
import { CarrierEditListComponent } from "../carrier-edit-list/carrier-edit-list.component";
import { DispatchEditComponent } from "../dispatch-edit/dispatch-edit.component";

@Component({
  selector: "carrier-list-table",
  templateUrl: "./carrier-list-table.component.html",
  styleUrls: ["./carrier-list-table.component.css"],
})
export class CarrierListTableComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  //#region Properties
  public gridName: string = "CarrierConfigGrid";
  public editActive = false;
  public enablePersistence: boolean = true;
  public filterCount: number = 0;
  public carrierId: number;
  public carrierLoading = true;
  public submitting: any;
  public editCarrier: Carrier = {};
  public truckTypes: TruckType[] = [];
  public editTruck: TruckType = {};
  public dispatchPhone: string = "Loading...";
  public pageSettings: PageSettingsModel = {
    pageSize: 50,
    pageSizes: [50, 100, 200],
  };
  public filterSettings: FilterSettingsModel = {
    mode: "Immediate",
    type: "Excel",
  };
  public toolbarOptions: string[] = [
    "ExcelExport",
   
  ];
  public allColumns: ColumnModel[] = [];
  public dataSource = { data: [] };
  public allCarriers: Carrier[] = [];
  public savingCarrierIds: Set<number> = new Set<number>();
  public highlightedCarrierId: number = null;
  private pendingScrollToCarrierId: number = null;
  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = [
    "name",
    "description",
    "truckType",
    "phoneNumber",
    "email",
    "integrationId",
    "isActive",
    "edit",
  ];
  //#endregion

  //#region Icon Properties
  public faPlus = faPlus;
  public faEdit = faEdit;
  public faPen = faPen;
  public faTrash = faTrash;
  public faTimes = faTimes;
  public faCheck = faCheck;
  public faDuePhone = faPhone;
  public faBan = faBan;
  //#endregion

  //#region ViewChild Properties
  @ViewChild("grid", { static: true })
  grid: GridComponent;

  @ViewChild("carrierEditTemplate", { static: true })
  public carrierEditTemplate: any;

  @ViewChild(MatPaginator, { static: false })
  paginator: MatPaginator;

  @ViewChild(MatSort, { static: false })
  sort: MatSort;
  //#endregion

  //#region Constructor with Dependencies
  constructor(
    private errorService: ErrorService,
    public carrierService: carrierService,
    public truckTypeService: truckTypeService,
    private dialogService: MatDialog,
    private platformConfigService: PlatformConfigService,
    private sessionService: SessionService,
    private kmlService: KmlService,
    private toastrService: NbToastrService,
    private filterService: KwFilterDropdownService
  ) {}
  //#endregion

  ngOnInit() {
    this.loadData();
    this.setColumns();
  }

  loadData() {
    setTimeout(() => {
      this.carrierLoading = true;

      forkJoin([this.carrierService.getCarriers()]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (x) => {
          this.allCarriers = x[0];

          this.carrierLoading = false;
          this.setColumns();
        }
      ),
        (err) => {
          this.errorService.handleError(err);
          this.carrierLoading = false;
        };
      this.platformConfigService.getDispatchPhone().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((phone) => {
        this.dispatchPhone = phone;
      });

      // this.truckTypeService.getTruckTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      //   (data) => {
      //     this.truckTypes = data;
      //     if (this.paginator)
      //       this.paginator._changePageSize(this.paginator.pageSize);
      //     this.carrierLoading = false;
      //   },
      //   (err) => {
      //     this.errorService.handleError(err);
      //     this.carrierLoading = false;
      //   }
      // );
    }, 1000);
  }

  toolbarClick(args) {
    this.carrierLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "Excel Export") {
      this.grid
        .excelExport({
          fileName: `CarrierExport.xlsx`,
        })
        .then(() => {
          this.carrierLoading = false;
        });
    }
  }

  setColumns() {
    this.allColumns = [];

    // Hidden primary key column - lets us look up/scroll to a specific row
    // (e.g. a newly created carrier).
    this.allColumns.push({
      field: "id",
      isPrimaryKey: true,
      visible: false,
    });

    this.allColumns.push(
      ...[
        {
          field: "name",
          headerText: "Carrier",
        },
        {
          field: "description",
          headerText: "Carrier Description",
        },
        {
          field: "truckType.name",
          headerText: "Truck Type",
        },
        {
          field: "email",
          headerText: "Email",
        },
        {
          field: "phoneNumber",
          headerText: "Phone Number",
        },
        {
          field: "integrationId",
          headerText: "Integration Id",
        },
        {
          headerText: "Action",
          template: this.carrierEditTemplate,
          width: 100,
        },
      ]
    );
  }

  addCarrierClicked(IsEdit: boolean, id?: number) {
    const carrierEditDialog = this.dialogService.open(
      CarrierEditListComponent,
      {
        width: "700px",
        data: {
          phone: this.dispatchPhone,
        },
      }
    );
    if (IsEdit) {
      this.editCarrier = {
        ...this.allCarriers.filter((u) => u.id === id)[0],
      };
      this.editTruck = {
        ...this.truckTypes.filter(
          (w) => w.id == this.editCarrier.truckTypeId
        )[0],
      };
      window.localStorage.setItem(
        "editCarrier" + this.editCarrier.name,
        JSON.stringify(this.editCarrier)
      );
      window.localStorage.setItem(
        "editTruck" + this.editTruck.name,
        JSON.stringify(this.editTruck)
      );
      this.sessionService.selectedCarrier = this.editCarrier;
      this.sessionService.selectedTruck = this.editTruck;
      let scrollToTop = window.setInterval(() => {
        let pos = window.pageYOffset;
        if (pos > 0) {
          window.scrollTo(0, pos - 20); // how far to scroll on each step
        } else {
          window.clearInterval(scrollToTop);
        }
      }, 16);
      this.editActive = true;
    }

    carrierEditDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.backgroundSave && result.carrier) {
        if (result.isNew) {
          this.createCarrierInBackground(result.carrier);
        } else {
          this.saveCarrierInBackground(result.carrier);
        }
      } else if (result) {
        this.loadData();
      }
      // this.dispatchPhone = result;
      window.localStorage.removeItem("editCarrier" + this.editCarrier.name);
      window.localStorage.removeItem("editTruck" + this.editTruck.name);
      this.sessionService.selectedCarrier = {};
      this.sessionService.selectedTruck = {};
    });
  }

  private saveCarrierInBackground(carrier: Carrier): void {
    const originalCarrier = this.allCarriers.find((c) => c.id === carrier.id);
    this.savingCarrierIds.add(carrier.id);
    this.insertOrReplaceCarrierRow(this.shapeCarrierForGrid(carrier, originalCarrier));
    this.refreshGridSoon();

    this.carrierService.putCarrierDTO(carrier).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.savingCarrierIds.delete(carrier.id);
        // Keep the filter dropdown cache in sync so other pages show the change immediately
        this.filterService.upsertCached('carrier', updated || carrier);

        // 204 No Content leaves `updated` null/undefined - fall back to the carrier we sent.
        const shaped = this.shapeCarrierForGrid(updated, originalCarrier);
        this.insertOrReplaceCarrierRow(shaped);

        this.refreshGridSoon();
        this.toastrService.success(
          "Carrier details successfully updated!",
          updated.name + " updated!"
        );
      },
      error: (err) => {
        this.savingCarrierIds.delete(carrier.id);
        if (originalCarrier) {
          this.insertOrReplaceCarrierRow(originalCarrier);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to update Carrier "' + carrier.name + '". Please try again.',
          "Save failed"
        );
      }
    });
  }

  private createCarrierInBackground(carrier: Carrier): void {
    // The carrier doesn't exist server-side yet, so show a temporary
    // placeholder row (negative id so it can't collide with a real one)
    // with the saving spinner until the server assigns a real id.
    const tempId = -Date.now();
    this.insertOrReplaceCarrierRow({ ...carrier, id: tempId });
    this.savingCarrierIds.add(tempId);

    this.carrierService.postCarrierDTO(carrier).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.filterService.upsertCached('carrier', created);
        const shaped = this.shapeCarrierForGrid(created, carrier);
        this.removeCarrierRow(tempId);
        this.savingCarrierIds.delete(tempId);
        this.insertOrReplaceCarrierRow(shaped);
        this.jumpToCarrierRow(shaped.id);
        this.toastrService.success(
          "Carrier successfully saved!",
          (shaped.name || carrier.name) + " created!"
        );
      },
      error: (err) => {
        this.removeCarrierRow(tempId);
        this.savingCarrierIds.delete(tempId);
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to create Carrier "' + (carrier.name || "carrier") + '". Please try again.',
          "Create failed"
        );
      }
    });
  }

  private removeCarrierRow(id: number): void {
    this.allCarriers = this.allCarriers.filter((c) => c.id !== id);
    this.refreshGridSoon();
  }

  private shapeCarrierForGrid(updated: Carrier, original?: Carrier): Carrier {
    return {
      ...updated,
      truckType: updated.truckType || (original && original.truckType)
    };
  }

  private insertOrReplaceCarrierRow(carrier: Carrier): void {
    const index = this.allCarriers.findIndex((c) => c.id === carrier.id);
    const next = this.allCarriers.slice();
    if (index !== -1) {
      next[index] = carrier;
    } else {
      next.push(carrier);
    }
    this.allCarriers = next;
    this.refreshGridSoon();
  }

  // Defers the grid refresh to the next macrotask so Angular has already
  // pushed the updated allCarriers/savingCarrierIds state into the grid's
  // own [dataSource] binding before we force it to re-render.
  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingCarrierIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedCarrierId != null && args.data.id === this.highlightedCarrierId) {
      args.row.classList.add('row-highlight-new');
    }
  }

  // Applies the green highlight directly (and times its removal) right when
  // the row is actually visible, instead of leaving rowDataBound to start
  // the clock the first time it happens to touch this row.
  private startHighlightFade(row: Element, id: number): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    rowElement.classList.add("row-highlight-new");
    if (this.highlightedCarrierId === id) {
      this.highlightedCarrierId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove("row-highlight-new");
    }, 2000);
  }

  // Scrolls the grid's own internal content viewport, not the page - a
  // plain row.scrollIntoView() walks every scrollable ancestor (including
  // the page), which in practice was moving the page while leaving the
  // grid's own internal scroll area untouched, so the row stayed hidden.
  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector(".e-content") as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: "auto" });
    } else if (typeof rowElement.scrollIntoView === "function") {
      rowElement.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }

  private isDefaultSortActive(): boolean {
    // Carrier's grid has no configured default sort column (unlike Blocks,
    // which always keeps allBlocks sorted by name to match). With no user
    // sort applied, allCarriers' array order matches the grid's row order,
    // so index math is reliable; skip goToPage under any active sort.
    const cols = (this.grid && this.grid.sortSettings && this.grid.sortSettings.columns) || [];
    return cols.length === 0;
  }

  private jumpToCarrierRow(id: number): void {
    this.highlightedCarrierId = id;
    // Scrolling happens once the grid actually finishes its next render
    // (see onGridDataBound) rather than after a guessed delay.
    this.pendingScrollToCarrierId = id;

    if (!this.grid || !this.isDefaultSortActive()) {
      // Can't reliably compute which page the row lands on under a custom
      // sort - it'll still get highlighted/scrolled to whenever it's next
      // rendered on whatever page the user navigates to.
      return;
    }

    // Defer past the refresh(es) already queued by insertOrReplaceCarrierRow/
    // removeCarrierRow so this pages using the grid's up-to-date data.
    setTimeout(() => {
      const index = this.allCarriers.findIndex((c) => c.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allCarriers.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
      // Already on the right page - the refresh queued by
      // insertOrReplaceCarrierRow will trigger dataBound shortly on its own.
    });
  }

  editDispatch() {
    const confRef = this.dialogService
      .open(DispatchEditComponent, {
        minWidth: "20%",
        data: {
          phone: this.dispatchPhone,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
        if(result){
          this.dispatchPhone = result;
        }
      });
  }

  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this carrier?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.carrierService.deleteCarrierDTO(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        this.filterService.removeCached('carrier', id);
        this.toastrService.success("Carrier successfully deleted!");
        this.loadData();
      },
      error:(error) => {
        this.errorService.handleError(error);
        console.error(error);
      }}
    );
  }

  clearFilters() {
    window.localStorage.setItem("gridCarrierConfigFilters", ""); // Here grid is component name and OrderDetails is component ID
    this.grid.clearFiltering();
    this.filterCount = 0;
  }

  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToCarrierId != null) {
      // goToPage() can fire this from a render that still has stale data
      // (queued just ahead of the refresh that actually adds the row), so
      // only clear the pending id once we've actually found and scrolled to
      // it - otherwise keep waiting for the next dataBound to try again.
      const id = this.pendingScrollToCarrierId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToCarrierId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        // Reassert the scroll position shortly after - sticky header/footer
        // repositioning can quietly reset it a moment later.
        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  telephone_click() {
    window.open("tel:" + this.dispatchPhone);
  }
  downloadFiles(args){
    this.carrierLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("Excel")) {
      this.grid
        .excelExport({
          fileName: `CarrierExport.xlsx`,
        })
        .then(() => {
          this.carrierLoading = false;
        });
    }
  }
}
